import styled from '@emotion/styled';
import { HeadingText30, Modal, Button, BodyTextSmall } from '@modernhealth/vital-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CostEstimation } from 'src/generated/webapp_gql';
import { CostModalTable } from './CostModalTable';

const Text = styled(BodyTextSmall)({
    marginBottom: 20,
});

const StyledButton = styled(Button)({
    width: '100%',
    marginTop: 10,
});

const StyledHeading = styled(HeadingText30)({
    marginBottom: 20,
});

const SectionWrapper = styled.div({
    marginBottom: 20,
});

interface Props {
    visible: boolean;
    onConfirm: () => void;
    onClose: () => void;
    costEstimation?: CostEstimation;
    type?: string | null;
}

export const CostModal: React.FC<Props> = ({
    visible,
    onClose,
    onConfirm,
    costEstimation,
    type,
}) => {
    const { t } = useTranslation();

    const isHDHP = type === 'HDHP';

    let perSessionCostTable;
    let explanation;
    let calculationText;

    if (isHDHP) {
        perSessionCostTable = [
            costEstimation?.remainingDeductible && [
                t('payments:cost_modal.remaining_deductible'),
                `$${costEstimation.remainingDeductible}`,
            ],
            costEstimation?.coinsurance && [
                t('payments:cost_modal.percentage'),
                `${costEstimation.coinsurance * 100}%`,
            ],
        ];
        explanation = t('payments:cost_modal.explanation.hdhp');
        calculationText = t('payments:cost_modal.cost_calculation.hdhp');
    } else {
        perSessionCostTable = [
            costEstimation?.copay && [t('payments:cost_modal.copay'), `$${costEstimation.copay}`],
            costEstimation?.outOfPocketRemaining && [
                t('payments:cost_modal.remaining_oop'),
                `$${costEstimation.outOfPocketRemaining}`,
            ],
        ];
        explanation = t('payments:cost_modal.explanation.ppo');
        calculationText = t('payments:cost_modal.cost_calculation.ppo');
    }

    const hdhpOutOfPocketTable = [
        t('payments:cost_modal.remaining_oop'),
        `$${costEstimation?.outOfPocketRemaining}`,
    ];

    return (
        <Modal isVisible={visible} onCloseButtonClick={onClose} maxWidth={500}>
            <SectionWrapper>
                <StyledHeading> {t('payments:cost_modal.title')} </StyledHeading>
                <Text>{t('payments:cost_modal.explanation')}</Text>
                <Text>{explanation}</Text>
            </SectionWrapper>
            <SectionWrapper>
                <StyledHeading> {t('payments:cost_modal.cost_calculation')} </StyledHeading>
                <Text>{calculationText}</Text>
                <CostModalTable data={perSessionCostTable} />
                {isHDHP && <Text>{t('payments:cost_modal.oop')}</Text>}
                {isHDHP && <CostModalTable data={[hdhpOutOfPocketTable]} />}
            </SectionWrapper>
            <StyledButton size="medium" onClick={onConfirm}>
                {t('payments:proceed_pay')}
            </StyledButton>
        </Modal>
    );
};
